import { Injectable } from '@angular/core';
import { createRemoteJWKSet, jwtVerify } from 'jose';
import { jwtDecode, JwtPayload } from 'jwt-decode';

interface VerifyTokenOptions {
  readonly audience: string;
  readonly issuer: string;
  readonly jwksPath: string;
}

@Injectable({ providedIn: 'root' })
export class JwtService {
  private readonly jwksByUrl = new Map<string, ReturnType<typeof createRemoteJWKSet>>();

  decodeToken(token: string): JwtPayload {
    try {
      return jwtDecode<JwtPayload>(token);
    } catch {
      return {};
    }
  }

  async verifyToken(token: string, options: VerifyTokenOptions): Promise<void> {
    await jwtVerify(token, await this.getRemoteJwks(options), {
      audience: options.audience,
      issuer: options.issuer,
    });
  }

  private async getRemoteJwks(options: VerifyTokenOptions) {
    const url = await this.getJwksUrl(options);
    const cachedJwks = this.jwksByUrl.get(url);

    if (cachedJwks) {
      return cachedJwks;
    }

    const jwks = createRemoteJWKSet(new URL(url));
    this.jwksByUrl.set(url, jwks);
    return jwks;
  }

  private async getJwksUrl({ issuer, jwksPath }: VerifyTokenOptions): Promise<string> {
    if (jwksPath) {
      return new URL(jwksPath, issuer.endsWith('/') ? issuer : `${issuer}/`).toString();
    }

    const discoveryUrl = new URL('/.well-known/openid-configuration', issuer).toString();
    const response = await fetch(discoveryUrl);
    const discoveryDocument = (await response.json()) as { jwks_uri?: unknown };

    if (typeof discoveryDocument.jwks_uri !== 'string') {
      throw new Error('OIDC discovery document does not include a jwks_uri.');
    }

    return discoveryDocument.jwks_uri;
  }
}
